import { ScheduleData } from './schedule-data.interface';
import { SchedulePosition } from './schedule-position.interface';
import { ServicesApi } from './../../shared/sdk/services/custom/Services';
import { PlayerManagementService } from './playermanagement.service';

import { LoopBackAuth } from './../../shared/sdk/services/core/auth.service';
import { PlayRequestEventData } from './PlayRequestEventData';
import { setInterval } from 'timers';
import { packageChunkSort } from '@angular/cli/utilities/package-chunk-sort';
import { LayoutPanel } from './../../shared/sdk/models/LayoutPanel';
import { ScreenCategory } from './../../shared/sdk/models/ScreenCategory';
import { RotationItem } from './../../shared/sdk/models/RotationItem';
import { Subscription } from 'rxjs/Subscription';
import { Observable } from 'rxjs/Observable';
import 'rxjs/add/operator/debounceTime';
import { CategoryRotationApi, Player } from '../../shared/sdk';
import { Screen } from '../../shared/sdk';
import { PlayerApi } from '../../shared/sdk';
import { PlayerService } from './player.service';
import { PlayerGroupApi } from '../../shared/sdk';
import { ScheduleTimeframe } from './../../shared/sdk/models/ScheduleTimeframe';
import { CategoryRotation } from './../../shared/sdk/models/CategoryRotation';
import { ScheduleRotation } from './../../shared/sdk/models/ScheduleRotation';
import { PlayerGroup } from './../../shared/sdk/models/PlayerGroup';
import { DataService } from '../../core.module/services/data.service';
import { Injectable, EventEmitter } from '@angular/core';
import * as moment from 'moment';
import { PlayerLogService } from './PlayerLog.service';
import { md5 } from '../../shared/md5';
import * as wf from 'webfontloader';
import { ScheduleState } from './schedule-state.interface';
import { PanelState } from './panel-state.interface';
import { PositionData } from './position-data.interface';
import { Subject } from 'rxjs/Subject';

// used to store the current postion within a rotation or screen category
interface NextScreenData {
  panelNum: number;
  schedulePosition: SchedulePosition;
  playRequestData: PlayRequestEventData;
  timeFromPlayerState: number;
  nextTickTime: number;
  resultText: string;
}

@Injectable()
export class ScheduleService {
  // events
  public scheduleUpdated$ = new Subject<ScheduleData>();
  public onScheduleLoaded = new EventEmitter<ScheduleData>();
  public onPlayRequest = new EventEmitter<PlayRequestEventData>();
  public onPreloadRequest = new EventEmitter<PlayRequestEventData>();
  public onPreloadClear = new EventEmitter<{ panelNum: number }>();
  public onStop = new EventEmitter<any>();
  public onNoContent = new EventEmitter<any>(); // no content avaiable event

  // timers
  public forceInterruptTimer;

  // create a new schedule state
  // holds overall schedule status like isPlaying
  public defaultScheduleState: ScheduleState = {
    scheduleData: {
      updateNeeded: true,
      scheduleHash: null,
      playerGroups: [],
      inactivePlayerGroups: [],
      categoryRotations: [],
      files: [],
      fileUrls: [],
      fontList: [],
      screenCount: 0,
      scheduleSize: 0,
      hasWeatherScreens: false,
      hasTriggerableScreens: false,
      hasAdvancedConditions: false,
      hasTimeframes: false,
      weatherZips: [],
      totalRuntime: 0,
      hasOverrideGroup: false,
      overridePlayerGroup: null,
      overrideGroupTimeframes: []
    }, // start with no schedule data
    isPlaying: false,
    lastScheduleLoad: null,
    lastScheduleCheck: null,
    nextScheduleCheck: null,
    hasContent: true,  // will update when schedule is started

    currentSchedulePosition: {
      lastTick: null,
      numPanels: -1,
      panelStates: [],
      hasActiveSchedule: false,
      forceTimeList: [],
      minTimeList: [],
      positions: [],

      hasPendingForceRotationItem: false,
      pendingForceRotationItem: null,
      pendingForcePanelIndex: -1,
      pendingForceCategoryRotationIndex: -1
    },
    nextSchedulePostion: null,
    previousSchedulePosition: null,
    currentPlayRequest: null,
    previousPlayRequest: null,
    nextPlayRequest: null,
    preloadRequest: null,
    dwellTimerStartDate: null,
    dwellTime: null,
    dwellTimerLeft: null,
    preloadTimerStartDate: null,
    preloadTimerLeft: null,
    preloadDwellTime: null
  };

  public currentScheduleState: ScheduleState = JSON.parse(JSON.stringify(this.defaultScheduleState));

  public dwellTimer: any;
  public preloadTimer: any;


  public timeLeftTimer: any;

  constructor(
    public logger: PlayerLogService,
    public servicesApi: ServicesApi,
    public playerApi: PlayerApi,
  ) {

  }

  resetScheduleState() {
    this.stop(); // stop playing
    this.currentScheduleState = JSON.parse(JSON.stringify(this.defaultScheduleState));
    this.loadSchedule(this.currentScheduleState.scheduleData);
  }

  async updateSchedule(playerId, forceReload) {
    try {
      this.logger.info('schedule', 'Update Schedule Triggerd');
      let scheduleData;

      if (forceReload) {
        scheduleData = await this.servicesApi.getSchedule(playerId).toPromise();
      } else {
        scheduleData = await this.servicesApi.getSchedule(playerId, this.currentScheduleState.scheduleData.scheduleHash).toPromise();
      }

      this.currentScheduleState.lastScheduleCheck = new Date();

      // console.log(scheduleData); // debug only
      if (scheduleData && scheduleData.updateNeeded) {

        this.loadSchedule(scheduleData);

        this.scheduleUpdated$.next(scheduleData);
      } else if (scheduleData && !scheduleData.updateNeeded) {
        this.logger.info('schedule', 'Schedule has not changed, no need to reload');
      } else if (!scheduleData) {
        this.logger.info('schedule', 'No schedule data received, using existing data');
      }

      return scheduleData;

    } catch (error) {
      this.logger.error('schedule', `updateSchedule() failed. Could not get schedule data via rest`, error);

      return error;
    } finally {

    }
  }

  loadSchedule(scheduleData: ScheduleData) {
    this.logger.info('schedule', 'Schedule load started');
    // load rotations
    if (scheduleData) {
      let indexCounter = -1;
      // process the updated rotation
      scheduleData.categoryRotations = scheduleData.categoryRotations.map((cr: any): CategoryRotation => {
        indexCounter += 1; // inc index counter for category rotations

        // add some convience props to each player in group
        if (!cr.scheduleTimeframes) { cr.scheduleTimeframes = []; }

        cr.scheduleTimeframes = cr.scheduleTimeframes.map((tf: ScheduleTimeframe): ScheduleTimeframe => {
          tf.start = new Date(tf.start);
          tf.end = new Date(tf.end);
          tf.endTime = new Date(tf.endTime);
          tf.startTime = new Date(tf.startTime);
          if (tf.specificDates) {
            tf.specificDates = tf.specificDates.map((sd: any) => { return new Date(sd); });
          }

          if (tf.playMode === 'force') {
            this.currentScheduleState.currentSchedulePosition.forceTimeList.push(
              {
                id: cr.id,
                rotationItem: null,
                categoryRotation: cr,
                categoryRotationIndex: indexCounter,
                panelIndex: -1,
                lastPlay: null,
                forceMins: tf.forcePlayTime,
                interrupt: tf.forceInterrupt
              }
            );
          }

          return tf;
        });

        // load the screenlayout and panels
        if (cr.screenLayout && cr.screenLayout.layoutPanels) {
          // stop playing to load new data
          this.stop();  // this will also stop all the dwell timers

          // set player state vars
          this.currentScheduleState.currentSchedulePosition.numPanels = cr.screenLayout.layoutPanels.length; // save num of panels
          this.currentScheduleState.currentSchedulePosition.panelStates = []; // clear panel states
          this.currentScheduleState.currentSchedulePosition.hasActiveSchedule = false; // show schedule as not active until first tick
          this.currentScheduleState.currentSchedulePosition.forceTimeList = [];
          this.currentScheduleState.currentSchedulePosition.minTimeList = [];
          this.currentScheduleState.currentSchedulePosition.positions = [];

          this.currentScheduleState.currentSchedulePosition.hasPendingForceRotationItem = false;
          this.currentScheduleState.currentSchedulePosition.pendingForceRotationItem = null;
          this.currentScheduleState.currentSchedulePosition.pendingForcePanelIndex = -1;
          this.currentScheduleState.currentSchedulePosition.pendingForceCategoryRotationIndex = -1;

          // go through each layout panel
          cr.screenLayout.layoutPanels = cr.screenLayout.layoutPanels.map((lp: LayoutPanel) => {

            // create new panel default state
            const panelState: PanelState = {
              panelNum: cr.screenLayout.layoutPanels.indexOf(lp),
              isPlaying: false,
              lastTick: null,
              categoryRotationIndex: -1,
              currentCategoryRotation: null,
              currentRotationItem: null,

              hasValidRotation: false,
              hasValidRotatoinItem: false,
              hasValidSchedule: false,
              dwellTime: -1,
            };

            this.currentScheduleState.currentSchedulePosition.panelStates.push(panelState);  // add to panelstate list

            lp.rotationItems = lp.rotationItems.map((rotationItem: any): RotationItem => {


              if (rotationItem.screenCategory && !rotationItem.screenCategory.childRotationItems) { rotationItem.screenCategory.childRotationItems = []; }
              if (rotationItem.ownerScreenCategory && !rotationItem.ownerScreenCategory.childRotationItems) { rotationItem.ownerScreenCategory.childRotationItems = []; }
              if (!rotationItem.scheduleTimeframes) { rotationItem.scheduleTimeframes = []; } // add empty timeframe list if none found

              if (!rotationItem.screenCategory) {
                rotationItem.screenCategoryId = null;
              }

              rotationItem.parentCategoryRotationId = cr.id;

              rotationItem.scheduleTimeframes = rotationItem.scheduleTimeframes.map((tf: ScheduleTimeframe): ScheduleTimeframe => {
                tf.start = new Date(tf.start);
                tf.end = new Date(tf.end);
                tf.endTime = new Date(tf.endTime);
                tf.startTime = new Date(tf.startTime);
                if (tf.specificDates) {
                  tf.specificDates = tf.specificDates.map((sd: any) => { return new Date(sd); });
                }

                // add this item to the forced time items
                // the schedule will
                if (tf.playMode === 'force') {
                  this.currentScheduleState.currentSchedulePosition.forceTimeList.push(
                    {
                      id: rotationItem.id,
                      rotationItem: rotationItem,
                      categoryRotation: null,
                      categoryRotationIndex: indexCounter,
                      panelIndex: cr.screenLayout.layoutPanels.indexOf(lp),
                      lastPlay: null,
                      forceMins: tf.forcePlayTime,
                      interrupt: tf.forceInterrupt
                    }
                  );
                }

                return tf;
              });

              // item is a screen on a rotation directly
              if (rotationItem.screen) {
                rotationItem.parentRotationItemId = null;
              }

              // item is a screen category
              // set the sort order of screens WITHIN a category
              if (rotationItem.screenCategory && rotationItem.screenCategory.childRotationItems) {

                rotationItem.screenCategory.childRotationItems = rotationItem.screenCategory.childRotationItems.map((ri: any): RotationItem => {
                  ri.parentCategoryRotationId = cr.id;
                  ri.parentRotationItemId = rotationItem.id;
                  ri.scheduleTimeframes = ri.scheduleTimeframes.map((tf: ScheduleTimeframe): ScheduleTimeframe => {
                    tf.start = new Date(tf.start);
                    tf.end = new Date(tf.end);
                    tf.endTime = new Date(tf.endTime);
                    tf.startTime = new Date(tf.startTime);
                    if (tf.specificDates) {
                      tf.specificDates = tf.specificDates.map((sd: any) => { return new Date(sd); });
                    }

                    return tf;
                  });

                  return ri;
                });
                // sort by position
                rotationItem.screenCategory.childRotationItems.sort(this.sortByPosition);
              }



              // index within rotation item
              rotationItem.currentIndex = -1;

              return rotationItem;
            });

            // sort by position
            lp.rotationItems.sort(this.sortByPosition);

            return lp;
          });

        }

        // index of current rotation item
        cr.currentIndex = -1;

        return cr;
      });

      // load the fonts needed by this schedule
      this.loadFonts(scheduleData.fontList);

      // save the new scheduledata to the current state
      this.currentScheduleState.scheduleData = scheduleData;

    } else {
      this.logger.info('schedule', 'Received Schedule was blank, resetting to default');
      this.currentScheduleState = JSON.parse(JSON.stringify(this.defaultScheduleState));
      this.loadSchedule(this.currentScheduleState.scheduleData);
      return;
    }

    // start force interrupt timer
    if (this.forceInterruptTimer) {
      clearInterval(this.forceInterruptTimer);
    }

    // create a force timer if any forced items actually exist
    if (this.currentScheduleState.currentSchedulePosition.forceTimeList.length) {
      this.forceInterruptTimer = setInterval(() => {
        for (const item of this.currentScheduleState.currentSchedulePosition.forceTimeList) {
          const lastPlayTime = moment().diff(item.lastPlay);
          if (!item.lastPlay || lastPlayTime >= item.forceMins * 1000) {
            // time to force play!

            if (item.categoryRotation) {
              // im not sure what it means to force an entire rotation?
              // this may not be a vaild configuration..
            }

            // check timefraem of item
            if (this.checkTimeframes(this.currentScheduleState.currentSchedulePosition, item.rotationItem.scheduleTimeframes, item.rotationItem.id) && item.rotationItem.enabled) {

              if (item.rotationItem) {
                this.currentScheduleState.currentSchedulePosition.pendingForceRotationItem = item.rotationItem;
                this.currentScheduleState.currentSchedulePosition.hasPendingForceRotationItem = true;
                this.currentScheduleState.currentSchedulePosition.pendingForcePanelIndex = item.panelIndex;
                this.currentScheduleState.currentSchedulePosition.pendingForceCategoryRotationIndex = item.categoryRotationIndex;
                this.logger.info('schedule', `A forced rotationitem becaame valid: ${item.rotationItem.id}...`);

                if (item.interrupt) {
                  this.logger.info('schedule', `It is set to force play, sending request to play now.`);
                  // play now
                  this.doTick(item.panelIndex);
                } else {
                  this.logger.info('schedule', `It will play when the current screen expires.`);
                }

                item.lastPlay = new Date();
              }
            }
          }

        }

      }, 3000);
    }

    this.currentScheduleState.lastScheduleLoad = new Date();

    this.logger.info('schedule', 'Schedule load completed');

    this.onScheduleLoaded.emit(scheduleData); // send signal that data was updated
  }

  loadFonts(fontList) {
    // get fonts ready
    const flatList = fontList.map(item => { return item.fontName; });

    if (flatList && flatList.length) {
      this.downloadFonts(flatList);
    }

  }

  downloadFonts(fontlist) {
    // this will not reload exitsing fonts, feel free to call as many times as needed
    wf.load({
      classes: false,
      google: {
        families: fontlist
      },
      loading: function () {
        // console.log('loading fonts ' + fontlist);
      },

    });
  }

  sortByPosition(a, b) {
    if (a.positionIndex < b.positionIndex) {
      return -1;
    }

    if (a.positionIndex > b.positionIndex) {
      return 1;
    }

    // a must be equal to b
    return 0;
  }

  // staring doing ticks for a panel
  play(panelNum?) {

    if (this.currentScheduleState.currentSchedulePosition.numPanels === -1) {
      return;
    }

    if (panelNum) {
      // play only specified panel
      if (this.currentScheduleState.isPlaying) {
        this.stop(); // stop first
      }
      this.logger.info('schedule', `Start playing schedule for panel: ${panelNum}`);
      this.doTick(panelNum);
    } else {
      // start playing all panels
      if (this.currentScheduleState.isPlaying) {
        this.stop(); // stop first
      }

      for (let index = 0; index < this.currentScheduleState.currentSchedulePosition.numPanels; index++) {
        this.logger.info('schedule', `Start playing schedule for panel: ${index}`);
         this.doTick(index);
      }

    }

    // time left timer
    if (this.timeLeftTimer) {
      clearInterval(this.timeLeftTimer);
      this.timeLeftTimer = null;
    }

    this.timeLeftTimer = setInterval(() => {
      if (this.currentScheduleState.dwellTimerStartDate && this.currentScheduleState.dwellTime) {
        this.currentScheduleState.dwellTimerLeft = this.currentScheduleState.dwellTime - moment(Date.now()).diff(this.currentScheduleState.dwellTimerStartDate, 'seconds');
      }

      // preload time
      if (this.currentScheduleState.preloadTimerStartDate && this.currentScheduleState.dwellTime && this.currentScheduleState.dwellTime > 0) {
        const preloadTickTime = this.currentScheduleState.dwellTime > 3 ? this.currentScheduleState.dwellTime - 3 : this.currentScheduleState.dwellTime / 2;
        this.currentScheduleState.preloadTimerLeft = preloadTickTime - moment(Date.now()).diff(this.currentScheduleState.preloadTimerStartDate, 'seconds');
      }

    }, 1000);

    this.currentScheduleState.isPlaying = true;
  }

  // regardless of current position of schedule, pause playback and immeadiatly play passed screen
  overridePlayingScreen(rotationItemId, checkIfTriggerable): PlayRequestEventData {

    // find what panel this screen is on and pause the stop the timeout

    let found = false;
    let foundRotationItem = null;
    let foundCategoryRotation: CategoryRotation = null;
    let panelNum = 0;
    let foundRotationItemIndex = null;

    // look at each category rotation
    for (const cr of this.currentScheduleState.scheduleData.categoryRotations) {
      if (found) { break; }
      foundCategoryRotation = cr;

      // look at each panel in rotation
      for (const p of cr.screenLayout.layoutPanels) {
        if (found) { break; }
        panelNum = cr.screenLayout.layoutPanels.indexOf(p); // set the current panel num

        // each rotation item
        for (const ri of p.rotationItems) {
          if (found) { break; }
          if (ri.id && ri.id === rotationItemId) {
            found = true;
            foundRotationItem = ri;
            foundRotationItemIndex = p.rotationItems.findIndex(item => { return item.id === ri.id; });
            break;
          }

          if (ri.screenCategoryId && ri.screenCategory.childRotationItems) {
            for (const cri of ri.screenCategory.childRotationItems) {
              if (cri.id && cri.id === rotationItemId) {
                found = true;
                foundRotationItem = cri;
                foundRotationItemIndex = ri.screenCategory.childRotationItems.findIndex(item => { return item.id === cri.id; });
                break;
              }
            }

          }
        }

      }
    }



    // only send update if schedule is active
    if (found && foundRotationItem) {

      // check playergroup constraints
      if (!this.checkPlayerGroups(this.currentScheduleState.scheduleData.playerGroups, foundRotationItem, foundCategoryRotation.id)) {
        this.logger.info('schedule', `OVERRIDE was requested for rotationitem '${rotationItemId}', but this player has no permission to play at this playergroupId`);
        return;
      }

      // check playergroup constraints
      if (checkIfTriggerable && !foundRotationItem.triggerable) {
        this.logger.info('schedule', `OVERRIDE was requested for rotationitem '${rotationItemId}', but triggering is not enabled for this screen`);
        return;
      }

      // cancel this existing dwell timer for this panel
      if (this.dwellTimer) {
        clearTimeout(this.dwellTimer);
      }

      // cancel preload
      if (this.preloadTimer) {
        clearTimeout(this.preloadTimer);
      }

      this.onPreloadClear.next({ panelNum: panelNum }); // notifiy player-main-component to not play any saved preload screen

      // do next tick after dwelltime for this screen, or 10 seconds if missing
      this.dwellTimer = setTimeout(() => {
        this.doTick(panelNum);
      }, foundRotationItem.dwellTime ? foundRotationItem.dwellTime * 1000 : 10000);
      this.currentScheduleState.currentSchedulePosition.panelStates[panelNum].dwellTime = foundRotationItem.dwellTime ? foundRotationItem.dwellTime * 1000 : 10000;

      // send request to play a screen
      const playRequestEventData: PlayRequestEventData = {
        rotationItem: foundRotationItem,
        rotationItemIndex: foundRotationItemIndex,
        panelIndex: panelNum,
        categoryRotationName: foundCategoryRotation.name,
        categoryRotationId: foundCategoryRotation.id
      };

      this.logger.info('schedule', `OVERRIDE: Requesting PLAY of rotationitem '${rotationItemId}' screen id: '${playRequestEventData.rotationItem.screenId}'`);
      this.onPlayRequest.emit(playRequestEventData);

      return playRequestEventData;
    } else {
      this.logger.info('schedule', `OVERRIDE was requested for rotationitem '${rotationItemId}', but this player has no matching screen id to play?`);
    }

  }

  jumpToScreen(rotationItemId) {
    const playrequest = this.overridePlayingScreen(rotationItemId, false);
    this.currentScheduleState.currentPlayRequest = playrequest; // update currently playing screen
  }

  stop(panelNum?) {

    // stop the preloader
    if (this.preloadTimer) {
      clearTimeout(this.preloadTimer);
    }
    this.onPreloadClear.next({ panelNum: panelNum }); // notifiy player-main-component to not play any saved preload screen

    if (panelNum) {
      this.logger.info('schedule', `Stop requested for panel '${panelNum}'`);
      if (this.dwellTimer) {
        clearTimeout(this.dwellTimer);
      }


    } else {
      this.logger.info('schedule', `Stop requested for all panels`);
      for (let index = 0; index < this.currentScheduleState.currentSchedulePosition.numPanels; index++) {
        if (this.dwellTimer) {
          clearTimeout(this.dwellTimer);
        }

        // stop the preloader
        this.onPreloadClear.next({ panelNum: index }); // notifiy player-main-component to not play any saved preload screen
      }

    }


    // time left timer
    if (this.timeLeftTimer) {
      clearInterval(this.timeLeftTimer);
    }

    this.currentScheduleState.hasContent = false;
    this.currentScheduleState.isPlaying = false;
    this.onStop.emit();
  }

  getNextScreen(panelNum, startingSchedulePosition, playerGroups, categoryRotations): NextScreenData {
    const newPlayerState = JSON.parse(JSON.stringify(startingSchedulePosition));  // do not alter the passed playerstate


    // init return value
    const nextScreenData: NextScreenData = {
      panelNum: panelNum,
      schedulePosition: newPlayerState,
      playRequestData: null,
      timeFromPlayerState: -1,
      nextTickTime: 3000, // set default tick time to 3sec
      resultText: ''
    };

    let saveRotationState = true;

    // init lasttick on first play
    if (newPlayerState.lastTick === null) {
      newPlayerState.lastTick = new Date();
    }

    // get the time diff from the passed player state
    const timesincelasttick = moment(new Date()).diff(newPlayerState.lastTick);
    nextScreenData.timeFromPlayerState = timesincelasttick;
    // this.logger.info('schedule', `Player tick (Panel ${panelNum}):  Last tick was ${timesincelasttick}ms ago...`);


    // set the start index the last index
    let rotationStartIndex = newPlayerState.panelStates[panelNum].categoryRotationIndex;

    // check for pending force play
    if (newPlayerState.hasPendingForceRotationItem) {
      newPlayerState.hasPendingForceRotationItem = false;

      // check if enabled
      if (newPlayerState.pendingForceRotationItem.enabled) {

        // item is a category
        if (newPlayerState.pendingForceRotationItem.ownerScreenCategoryId) {
          rotationStartIndex = newPlayerState.pendingForceCategoryRotationIndex - 1;
          saveRotationState = false;
        }

        // item is a screen
        if (newPlayerState.pendingForceRotationItem.screenId) {
          newPlayerState.panelStates[panelNum].currentRotationItem = newPlayerState.pendingForceRotationItem;

          // send request to play a screen
          const playRequest: PlayRequestEventData = {
            rotationItem: newPlayerState.pendingForceRotationItem,
            rotationItemIndex: null,
            panelIndex: panelNum,
            categoryRotationName: 'Unknown',
            categoryRotationId: null
          };

          nextScreenData.playRequestData = playRequest;
          nextScreenData.nextTickTime = newPlayerState.pendingForceRotationItem.dwellTime ? newPlayerState.pendingForceRotationItem.dwellTime * 1000 : 20000;

        }
        // save last tick
        newPlayerState.lastTick = null;
        newPlayerState.lastTick = new Date(); // set last tick


        // extend rotation item to include the current rotation id
        if (nextScreenData && nextScreenData.playRequestData.rotationItem) {
          (<any>nextScreenData.playRequestData.rotationItem).playingInCategoryRotationId = nextScreenData.playRequestData.categoryRotationId;
        }


        return nextScreenData;
      }


    }

    // get next valid rotation / rotationitem
    const rotationData = this.getNextValidRotation(newPlayerState, categoryRotations, rotationStartIndex, panelNum, playerGroups);
    if (saveRotationState) {
      newPlayerState.panelStates[panelNum].hasValidRotation = rotationData.valid;
    }

    if (rotationData.valid) {
      // save changes to playerstate
      if (saveRotationState) {
        newPlayerState.panelStates[panelNum].categoryRotationIndex = rotationData.index;
        newPlayerState.panelStates[panelNum].currentCategoryRotation = rotationData.categoryRotation;
      }
      const positionData = this.getIndexPosition(newPlayerState, rotationData.categoryRotation.screenLayout.layoutPanels[panelNum].id); // get next valid rotation item in this panel
      const startIndex = positionData.currentIndex;
      const incrementToNextItem = positionData.playCount < positionData.playCountTarget ? false : true;

      const riData = this.getNextValidRotationItem(newPlayerState, playerGroups, rotationData.categoryRotation.screenLayout.layoutPanels[panelNum].rotationItems, startIndex, incrementToNextItem, rotationData.categoryRotation);

      // if the index is different from the last tick, reset counters
      if (riData.previousIndex !== riData.index) {
        positionData.playCount = 0;
      }

      newPlayerState.panelStates[panelNum].hasValidRotatoinItem = riData.valid;

      if (riData.valid) {
        // save changes to playerstate

        // set the current rotation item to play next
        newPlayerState.panelStates[panelNum].currentRotationItem = riData.rotationItem;

        let newPlayCount = positionData.playCount + 1; // inc playcount

        const inlineOrRandom = riData.random ? 'RANDOMLY' : 'IN ORDER';
        nextScreenData.resultText = `Staging item ${positionData.playCount + 1} of (screens per play turn) ${riData.screensPerPlay} ${inlineOrRandom} from screenCategory ${riData.rotationItem.ownerScreenCategoryId || `-NONE: screen directly on rotation, `} current index ${riData.index} previous index ${riData.previousIndex}, dwelltime: ${riData.rotationItem.dwellTime}, transition: ${riData.rotationItem.transition}`;

        this.updateIndexPostion(newPlayerState, rotationData.categoryRotation.screenLayout.layoutPanels[panelNum].id, riData.index, newPlayCount, riData.screensPerPlay); // save current index of item within current category rotation

        // playcount met, reset to 1
        if ((positionData.playCount + 1) >= riData.screensPerPlay) {
          newPlayCount = 1;
        }

        // item is screen on a rotation panel
        if (riData.rotationItem.layoutPanelId) {
          this.updateIndexPostion(newPlayerState, riData.rotationItem.id, riData.index, -1, -1); // save current index
        }

        // item is screen, within a category
        if (riData.rotationItem.ownerScreenCategoryId) {
          this.updateIndexPostion(newPlayerState, riData.rotationItem.ownerScreenCategoryId + (<any>riData.rotationItem).parentCategoryRotationId, riData.childIndex, -1, -1); // save current index
        }

        // save next tick time
        nextScreenData.nextTickTime = riData.rotationItem.dwellTime ? riData.rotationItem.dwellTime * 1000 : 10000;

        // mark schedule as ok to play
        newPlayerState.hasActiveSchedule = true;
      } else {
        // no rotationItem in rotation is valid
        newPlayerState.hasActiveSchedule = false;

        // check for valid rotation again in 3 seconds
        nextScreenData.nextTickTime = 3000;
      }

    } else {
      // no valid rotation found
      if (saveRotationState) {
        newPlayerState.hasActiveSchedule = false;
      }

      // mark check for valid rotation again in 3 seconds
      nextScreenData.nextTickTime = 3000;
    }

    // send request to play a screen (if a valid rotationitem was found)
    if (newPlayerState.hasActiveSchedule) {
      const playRequestEventData: PlayRequestEventData = {
        rotationItem: newPlayerState.panelStates[panelNum].currentRotationItem,
        rotationItemIndex: newPlayerState.panelStates[panelNum].categoryRotationIndex,
        panelIndex: panelNum,
        categoryRotationName: rotationData.categoryRotation.name,
        categoryRotationId: rotationData.categoryRotation.id
      };

      // save playrequest data
      nextScreenData.playRequestData = playRequestEventData;
    } else {

      nextScreenData.resultText = 'No screen is eligable to play';
    }

    // save last tick
    newPlayerState.lastTick = null;
    newPlayerState.lastTick = new Date(); // set last tick time

    return nextScreenData;
  }

  // find the next valid screen to play, advances one positoin forward

  doTick(panelNum) {

    // calc next screen
    this.logger.info('schedule', `Getting next screen...`);
    const nextScreenData = this.getNextScreen(panelNum, this.currentScheduleState.currentSchedulePosition, this.currentScheduleState.scheduleData.playerGroups, this.currentScheduleState.scheduleData.categoryRotations);
    this.logger.info('schedule', `Result: ${nextScreenData.resultText}`);

    // get preload screen Data
    this.logger.info('schedule', `Getting preload screen...`);
    const prelaodScreenData = this.getNextScreen(panelNum, nextScreenData.schedulePosition, this.currentScheduleState.scheduleData.playerGroups, this.currentScheduleState.scheduleData.categoryRotations);
    this.logger.info('schedule', `Preload result: ${prelaodScreenData.resultText}`);

    // only send update if schedule is active
    if (nextScreenData.schedulePosition.hasActiveSchedule) {
      this.currentScheduleState.hasContent = true;
      this.logger.info('schedule', `Requesting PLAY of rotation item id: '${nextScreenData.playRequestData.rotationItem.id}' screenId: '${nextScreenData.playRequestData.rotationItem.screenId}' (CR:${nextScreenData.playRequestData.categoryRotationName})`);
      this.currentScheduleState.previousPlayRequest = JSON.parse(JSON.stringify(this.currentScheduleState.currentPlayRequest));
      this.currentScheduleState.currentPlayRequest = nextScreenData.playRequestData;
      this.currentScheduleState.nextPlayRequest = prelaodScreenData.playRequestData;
      this.currentScheduleState.dwellTimerLeft = nextScreenData.nextTickTime / 1000;
      this.currentScheduleState.dwellTime = nextScreenData.nextTickTime / 1000;
      this.currentScheduleState.preloadTimerStartDate = null;
      this.currentScheduleState.preloadTimerLeft = null;
      this.currentScheduleState.preloadDwellTime = prelaodScreenData.nextTickTime / 1000;
      this.currentScheduleState.preloadRequest = null;
      this.onPlayRequest.emit(nextScreenData.playRequestData);
    } else {
      this.currentScheduleState.preloadRequest = null;
      this.currentScheduleState.hasContent = false;
      this.logger.info('schedule', `No item to play, will re-check in 3 seconds`);
      this.currentScheduleState.currentPlayRequest = null;
      this.onNoContent.emit();
    }

    // start preload timer - 3 sec before screen ends
    if (prelaodScreenData.schedulePosition.hasActiveSchedule && nextScreenData.schedulePosition.hasActiveSchedule) {
      if (this.preloadTimer) {
        clearTimeout(this.preloadTimer);
        this.preloadTimer = null;
      }

      // calc when preload should startm
      const preloadTickTime = nextScreenData.nextTickTime > 3000 ? nextScreenData.nextTickTime - 3000 : nextScreenData.nextTickTime / 2;
      this.currentScheduleState.preloadTimerLeft = preloadTickTime / 1000;
      this.logger.info('schedule', `Will start PRE-LOAD of next screen in ${preloadTickTime}ms`);

      this.preloadTimer = setTimeout(() => {
        this.logger.info('schedule', `Requesting PRE-LOAD of rotation item id: '${prelaodScreenData.playRequestData.rotationItem.id}' screenId: '${prelaodScreenData.playRequestData.rotationItem.screenId}' (CR:${prelaodScreenData.playRequestData.categoryRotationName})`);
        this.onPreloadRequest.next(prelaodScreenData.playRequestData);
        this.currentScheduleState.preloadRequest = prelaodScreenData.playRequestData;

      }, preloadTickTime);
      this.currentScheduleState.preloadTimerStartDate = new Date();

    } else {
      // no preload screen found
    }


    // do tick after time returned from nextScreen
    if (this.dwellTimer) {
      clearTimeout(this.dwellTimer);
      this.dwellTimer = null; // gc
    }
    this.dwellTimer = setTimeout(() => {
      this.doTick(panelNum);
    }, nextScreenData.nextTickTime);
    this.currentScheduleState.dwellTimerStartDate = new Date();

    // save current state
    this.currentScheduleState.previousSchedulePosition = JSON.parse(JSON.stringify(this.currentScheduleState.currentSchedulePosition));
    this.currentScheduleState.currentSchedulePosition = nextScreenData.schedulePosition;
  }

  getNextValidRotation(schedulePosition, categoryRotations, startIndex: number, panelNum: number, playerGroups): { valid: boolean, categoryRotation: CategoryRotation, index: number, previousIndex: number } {
    // return data
    const rotationData = {
      valid: false,
      categoryRotation: null,
      index: -1,
      previousIndex: -1
    };

    // save starting position
    const tempCategoryRotationIndex = schedulePosition.panelStates[panelNum].categoryRotationIndex;
    const tempcurrentCategoryRotation = schedulePosition.panelStates[panelNum].currentCategoryRotation;

    // loop through rotations, get next valid
    if (categoryRotations.length) {

      // inc category rotation

      rotationData.previousIndex = startIndex;

      let tempIndex = startIndex;
      do {
        if (tempIndex !== startIndex && startIndex === -1) {
          startIndex = 0;
        }

        // see if we have played everything in this rotation before moving onto the next
        const roundRobin = false;

        if (!roundRobin && tempcurrentCategoryRotation) {
          // play everything in each rotation before moving to the next

          // get last item in list
          const panelPos = this.getIndexPosition(schedulePosition, tempcurrentCategoryRotation.screenLayout.layoutPanels[panelNum].id);
          const lastRIindex = tempcurrentCategoryRotation.screenLayout.layoutPanels[panelNum].rotationItems.length - 1;
          const playCountReached = panelPos.playCount < panelPos.playCountTarget ? false : true;

          if (tempcurrentCategoryRotation.screenLayout.layoutPanels[panelNum].rotationItems.length && panelPos.currentIndex >= lastRIindex) {
            const lastRi: RotationItem = tempcurrentCategoryRotation.screenLayout.layoutPanels[panelNum].rotationItems[lastRIindex];

            // if last item is screencategory, get last item in screen category
            if (lastRi.screenCategory && lastRi.screenCategory.childRotationItems.length) {
              const lastChildRiIndex = lastRi.screenCategory.childRotationItems.length - 1;
              const childPositionData = this.getIndexPosition(schedulePosition, lastRi.screenCategoryId + tempcurrentCategoryRotation.id);

              if (childPositionData.currentIndex === lastChildRiIndex || playCountReached) {
                // we are on the last item on the last category, move to the next rotation now
                tempIndex += 1;
              }

            } else {
              tempIndex += 1;
            }
          } else {

            if (playCountReached  && panelPos.currentIndex >= lastRIindex) {
              tempIndex += 1;
            }


          }

        } else {
          // using round robin scheduling, always go to the next rotation
          tempIndex += 1;
        }


        // go to beginning at overflow
        if (tempIndex > categoryRotations.length - 1) {
          tempIndex = 0; // roll over to the beginning
        }

        const rotation = categoryRotations[tempIndex];

        // see if this rotation is allowed to play
        if (this.checkTimeframes(schedulePosition, rotation.scheduleTimeframes, rotation.id)) {

          // move the schedule position to this rotation, and check for vaild items within the rotation
          schedulePosition.panelStates[panelNum].categoryRotationIndex = tempIndex;
          schedulePosition.panelStates[panelNum].currentCategoryRotation = rotation;

          const positionData = this.getIndexPosition(schedulePosition, rotation.screenLayout.layoutPanels[panelNum].id); // get next valid rotation item in this panel
          const istartIndex = positionData.currentIndex;
          const incrementToNextItem = positionData.playCount < positionData.playCountTarget ? false : true;

          const riData = this.getNextValidRotationItem(schedulePosition, playerGroups, rotation.screenLayout.layoutPanels[panelNum].rotationItems, istartIndex, incrementToNextItem, rotation);

          if (riData.valid) {
            rotationData.categoryRotation = rotation;
            rotationData.index = tempIndex;
            rotationData.valid = true;
          }


        }
      } while (!rotationData.valid && tempIndex !== startIndex);

    }

    // put the schedule back to the starting value if nothing valid was found in any rotation
    if (!rotationData.valid) {
      schedulePosition.panelStates[panelNum].categoryRotationIndex = tempCategoryRotationIndex;
      schedulePosition.panelStates[panelNum].currentCategoryRotation = tempcurrentCategoryRotation;
    }


    return rotationData;
  }

  getNextValidRotationItem(schedulePosition, playerGroups, rotationItems: RotationItem[], startIndex: number, incrementToNextItem: boolean, parentCategoryRotation: CategoryRotation, random = false): { valid: boolean, rotationItem: RotationItem, previousIndex: number, index: number, screensPerPlay: number, childIndex: number, random: boolean } {

    // return data
    const itemData = {
      valid: false,
      rotationItem: null,
      previousIndex: -1,
      index: -1,
      screensPerPlay: 1,
      childIndex: -1,
      random: false
    };


    // inc through rotation items
    itemData.previousIndex = startIndex;

    let tempIndex = startIndex;

    // find valid rotation item
    do {
      // move to next item in list
      if (incrementToNextItem) {
        tempIndex += 1;
      }


      // go to beginning at overflow
      if (tempIndex > rotationItems.length - 1) {
        tempIndex = 0; // roll over to the beginning
      }

      // check that panel contains rotationitems
      if (rotationItems.length) {

        // get this rotation item
        let rotationItem: RotationItem;

        if (random) {
          // grab index randomly
          tempIndex = Math.floor(Math.random() * (rotationItems.length));
        }

        rotationItem = rotationItems[tempIndex];

        // item is a Screen
        if (rotationItem.screenId) {

          // see if this rotation is allowed to play
          if (this.checkTimeframes(schedulePosition, rotationItem.scheduleTimeframes, rotationItem.id) && this.checkPlayerGroups(playerGroups, rotationItem, parentCategoryRotation.id) && rotationItem.enabled && this.checkTriggering(rotationItem)) {
            itemData.rotationItem = rotationItem;
            itemData.valid = true;
            itemData.index = tempIndex;
          }
        }

        // item is a category, get next valid item from it
        if (rotationItem.screenCategoryId) {

          // check timeframe of category
          if (this.checkTimeframes(schedulePosition, rotationItem.scheduleTimeframes, rotationItem.id) && rotationItem.enabled && this.checkTriggering(rotationItem)) {

            // const childItem = this.getNextValidItemInScreenCategory(rotationItem);
            const childPositionData = this.getIndexPosition(schedulePosition, rotationItem.screenCategoryId + parentCategoryRotation.id);
            const childStartIndex = childPositionData.currentIndex;
            const childItem = this.getNextValidRotationItem(schedulePosition, playerGroups, rotationItem.screenCategory.childRotationItems, childStartIndex, true, parentCategoryRotation, rotationItem.random);

            if (childItem.valid) {

              // check timeframe of item within category
              if (this.checkTimeframes(schedulePosition, childItem.rotationItem.scheduleTimeframes, childItem.rotationItem.id) && this.checkPlayerGroups(playerGroups, childItem.rotationItem, parentCategoryRotation.id) && childItem.rotationItem.enabled && this.checkTriggering(rotationItem)) {
                itemData.rotationItem = childItem.rotationItem;
                itemData.valid = true;
                itemData.index = tempIndex;
                itemData.childIndex = childItem.index;
                itemData.screensPerPlay = rotationItem.screensPerPlay;
                itemData.random = rotationItem.random;
              }
            }
          }
        }

      } else {
        break; // no items in list
      }

      // normalize start index once the end of category or playlist is reached
      if (startIndex === -1 && tempIndex >= rotationItems.length - 1) { startIndex = 0; }

    } while (!itemData.valid && tempIndex !== startIndex);

    return itemData;
  }

  // check triggering
  checkTriggering(rotationItem: RotationItem) {
    if (rotationItem.triggerable && !rotationItem.inScheduleWhenTriggerEnabled) {
      return false;
    }
    return true;
  }

  // go through each time like a filter, first failure returns false
  checkTimeframes(schedulePosition, timeframes: ScheduleTimeframe[], itemIdForMinTime: string, compareDate?: any): boolean {
    let refDate = moment(new Date()); // by default make refDate now

    // set curent date to compare againest
    if (compareDate) { refDate = moment(compareDate); } // convert refDate to moment

    // look for time filter failures
    for (const tf of timeframes) {

      // check if before start date
      if (refDate.isBefore(tf.start, 'days')) { return false; }

      // check if after end date
      if (tf.hasEnd && refDate.isAfter(tf.end, 'days')) { return false; }

      // check days of week match
      if (tf.repeat) {
        if (tf.repeatDays) {
          // check if reference day is in repeatDays list
          if ((<Array<Number>>tf.repeatDays).indexOf((refDate.day() % 7)) === -1) { return false; }
          // check the repeat every __x__ weeks field, return false if not enough time has passed since last play
          // start counting from when the schedule started
          const weeks = (tf.repeatFrequency * 7) - 7 + 1;
          const weeksSinceStart = refDate.diff(tf.start, 'weeks');
          if ((weeksSinceStart % tf.repeatFrequency) !== 0) { return false; }
        }

      }

      // check specific days match
      if (!tf.repeat) {
        if (tf.specificDates) {
          let found = false;
          for (const d of tf.specificDates) {
            if (refDate.isSame(d, 'day')) { found = true; }
          }
          if (!found) { return false; }
        }
      }

      // check within start time, end time (HOURS AND MINUTES ONLY, ignore date)
      if (tf.specificTime) {
        const startTime = moment(tf.startTime).minutes() + moment(tf.startTime).hours() * 60;
        const endTime = moment(tf.endTime).minutes() + moment(tf.endTime).hours() * 60;
        const curTime = refDate.minutes() + refDate.hours() * 60; // convert reference date to mins of day
        if (curTime < startTime) { return false; } // check if before start time
        if (curTime > endTime) { return false; } // check if after end time
      }

      // check mintime between plays constraint
      if (tf.playMode === 'minTime' && itemIdForMinTime && schedulePosition) {
        const lastPlay = schedulePosition.minTimeList.find(item => { return item.id === itemIdForMinTime; });
        if (lastPlay) {
          const lastMinutes = moment(refDate).diff(lastPlay.date, 'minute');
          if (lastMinutes < tf.minTimeBetweenPlays) { return false; } // check if mintime has elapsed
          const index = schedulePosition.minTimeList.indexOf(lastPlay); // get index of item in mintime list
          schedulePosition.minTimeList.splice(index, 1, { id: itemIdForMinTime, date: new Date() }); // replace the mintime list item with a new one
        } else {
          schedulePosition.minTimeList.push({ id: itemIdForMinTime, date: new Date() });
        }

      }


      if (tf.playMode === 'inTurn') {
        // do nothing! this is the default
      }

    }

    refDate = null;
    return true;
  }

  checkPlayerGroups(playerGroups, rotationItem: RotationItem, categoryRotationId: string): boolean {

    if (rotationItem.screen && rotationItem.screen.playerGroups && playerGroups) {

      let okToPlay = false;
      for (const screenPG of rotationItem.screen.playerGroups) {
        // try to find a matching player group for this screen
        const foundPG = playerGroups.find(item => { return item.id === screenPG.id; });

        // make sure the screen's playergroup is category rotation of the players player group
        let found = false;
        if (foundPG && foundPG.categoryRotationId === categoryRotationId) {
          found = true;
        }

        if (found) {
          okToPlay = true;
          break;
        }
      }

      if (okToPlay) {
        return true;
      } else {
        return false;
      }

    }

    return false;
  }


  getIndexPosition(schedulePosition, searchId): PositionData {
    let result = <PositionData>{
      currentIndex: -1,
      id: null,
      playCount: 0
    };
    if (schedulePosition.positions && schedulePosition.positions.length) {
      const positionData = schedulePosition.positions.find(item => { return item.id === searchId; });
      if (positionData) {
        result = positionData;
      }
    }

    return result;
  }


  updateIndexPostion(playerState, id, index, playCount, playCountTarget) {
    const positionData = playerState.positions.find(item => { return item.id === id; });
    if (positionData) {
      // replace item in the array with this one
      const positionDataIndex = playerState.positions.indexOf(positionData);

      playerState.positions.splice(positionDataIndex, 1, { id: id, currentIndex: index, playCount: playCount, playCountTarget: playCountTarget });
    } else {
      // add new item to list
      playerState.positions.push({ id: id, currentIndex: index, playCount: playCount, playCountTarget: playCountTarget });
    }

  }


  getRotationItemList(compareDate?: Date): RotationItem[] {
    if (!compareDate) {
      compareDate = new Date();
    }
    const screenListRotations = JSON.parse(JSON.stringify(this.currentScheduleState.scheduleData.categoryRotations));
    const riList: RotationItem[] = [];


    for (const rotation of screenListRotations) {

      // make playergroup string
      (<any>rotation).playerGroupStr = '';
      for (const pg of rotation.playerGroups) {
        (<any>rotation).playerGroupStr = pg.name + ' ,';
      }

      if ((<any>rotation).playerGroupStr.length >= 2) {
        (<any>rotation).playerGroupStr = (<any>rotation).playerGroupStr.substring(0, (<any>rotation).playerGroupStr.length - 2);
      }

      // make rotationitem nodes
      for (const lp of rotation.screenLayout.layoutPanels) {

        let riIndex = -1;
        for (const ri of lp.rotationItems) {

          riIndex += 1;
          // add screen
          if (ri.screen) {
            // extend rotationitem class
            if (this.getSkipReasons(ri, null, rotation, compareDate).length === 0) {
              (<any>ri).categoryRotationName = rotation.name;
              (<any>ri).index = riIndex;
              riList.push(ri);
            }

          }
          // add screens from category
          if (ri.screenCategory) {

            let criIndex = -1;
            // add ri's of category
            for (const cri of ri.screenCategory.childRotationItems) {
              criIndex += 1;
              // extend rotationitem class
              if (this.getSkipReasons(cri, ri, rotation, compareDate).length === 0) {
                (<any>cri).categoryRotationName = rotation.name;
                (<any>cri).ownerScreenCategoryName = ri.screenCategory.name;
                (<any>cri).index = criIndex;
                riList.push(cri);
              }
            }

          }

        }

      }

    }


    return riList;

  }
  getSkipReasons(ri: RotationItem, categoryRi: RotationItem, rotation, compareDate: Date): string[] {
    const sr: string[] = [];
    if (ri.screen) {
      if (!this.checkTimeframes(null, ri.scheduleTimeframes, null, compareDate)) {
        sr.push(`Screen is outside it's scheduled timeframe`);
      }
    }

    if (ri.triggerable && !ri.inScheduleWhenTriggerEnabled) {
      sr.push(`Triggering is enabled, but 'Include when not triggered is disabled'`);
    }

    if (!ri.enabled) {
      sr.push(`Screen is part of schedule, but is set to disabled`);
    }

    if (categoryRi) {
      if (!this.checkTimeframes(null, categoryRi.scheduleTimeframes, null, compareDate)) {
        sr.push(`Screen is in a category that is outside it's scheduled timeframe`);
      }

      if (!categoryRi.enabled) {
        sr.push(`Screen is part of schedule, but its's category${categoryRi.screenCategory ? ' (' + categoryRi.screenCategory.name + ')' : ''} is set to disabled`);
      }
    }

    if (rotation) {
      if (!this.checkTimeframes(null, rotation.scheduleTimeframes, null, compareDate)) {
        sr.push(`Screen is in a Rotation that is outside it's scheduled timeframe`);
      }
    }

    return sr;
  }


}





// WEBPACK FOOTER //
// ./src/app/player/services/schedule.service.ts